import { FieldSchemaInterface, SchemaDataInterface } from '../../../../@core/interfaces/api-schema.interface';
import { FilterParam, RangeFilterParam } from '../../../../@core/interfaces/elastic-query.interface';
import { ExplorePayload, Filter, OmixAtlasTableColumns } from '../../../../@core/interfaces/omix-atlas.interface';


/**
 * getSearchFilterString function is used to create the Message
 * in the top of list of all datasets, which includes
 * @param searchValue Search string from input
 * @param filterStatus Selected filters and Selected Tiers
 * @param rangeFilter Selected range filters
 * @returns a combined message of top three
 */
export function getSearchFilterString(
  searchValue: string | undefined,
  filterStatus: FilterParam,
  rangeFilter: RangeFilterParam
): Filter[] {
  const filters: Filter[] = [];

  if (Object.keys(filterStatus).length === 0 && searchValue === '' && Object.keys(rangeFilter).length === 0) {
    return [];
  }

  for (const key in filterStatus) {
    filters.push(...filterStatus[key].map((filterVal) => ({ label: toTitleCaseString(filterVal) })));
  }

  if (searchValue !== '') {
    filters.push({ label: `${searchValue}`});
  }

  if (!!rangeFilter){
    if (rangeFilter.total_num_samples && Object.keys(rangeFilter.total_num_samples).length > 0) {
      const rangeSampleFilterString =
        rangeFilter.total_num_samples.gte === undefined
          ? `<=${rangeFilter.total_num_samples.lte} Samples`
          : rangeFilter.total_num_samples.lte !== undefined
            ? `${rangeFilter.total_num_samples.gte}-${rangeFilter.total_num_samples.lte} Samples`
            : `>=${rangeFilter.total_num_samples.gte} Samples`;
      filters.push({ label: toTitleCaseString(rangeSampleFilterString) });
    }
    if (rangeFilter.total_num_cells && Object.keys(rangeFilter.total_num_cells).length > 0) {
      const rangeCellFilterString =
        rangeFilter.total_num_cells.gte === undefined
          ? `<=${rangeFilter.total_num_cells.lte} Cells`
          : rangeFilter.total_num_cells.lte !== undefined
            ? `${rangeFilter.total_num_cells.gte} - ${rangeFilter.total_num_cells.lte} Cells`
            : `>=${rangeFilter.total_num_cells.gte} Cells`;
      filters.push({ label: toTitleCaseString(rangeCellFilterString) });
    }
  }
  return filters;
}

export function getExplorePayload(
  searchValue: string | undefined,
  sortField: string,
  sortType: string,
  searchAfter: (string | number)[] | string | undefined,
  filterParam: FilterParam,
  rangeFilter: RangeFilterParam): ExplorePayload {
  const payload: ExplorePayload = {
    data: {
      attributes: {
        method: { get : [searchValue && searchValue !== '' ? 'studies' : 'datasets', 'filters'] },
        search_bar_text: searchValue || '',
        samples_per_dataset: { min: null, max: null },
        cells_per_dataset: { min: null, max: null },
        selected_filters: filterParam,
        sort_by: sortField,
        sort_order: sortType
      },
      type: 'omixatlases'
    }
  };
  if(searchAfter){
    payload.data.attributes.sort_last_hit_val = searchAfter;
  }
  if (rangeFilter && rangeFilter.total_num_samples) {
    payload.data.attributes.samples_per_dataset = {
      min: rangeFilter.total_num_samples.gte || null,
      max: rangeFilter.total_num_samples.lte || null
    };
  }
  if (rangeFilter && rangeFilter.total_num_cells) {
    payload.data.attributes.cells_per_dataset = {
      min: rangeFilter.total_num_cells.gte || null,
      max: rangeFilter.total_num_cells.lte || null
    };
  }
  return payload;
}

export function toTitleCaseString(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

export const processTableDefFromSchema = (
  tableColumns: OmixAtlasTableColumns[],
  filterSchema: FieldSchemaInterface | undefined
): OmixAtlasTableColumns[] => {
  const columnKeys = ['total_num_samples', 'dataset_id', 'description'];
  const tableColumn: OmixAtlasTableColumns[] = [...tableColumns];
  if (filterSchema) {
    Object.keys(filterSchema).forEach((datasetKey: string) => {
      let schemaData: SchemaDataInterface;
      Object.keys(filterSchema[datasetKey]).forEach((dataKey: string) => {
        schemaData = filterSchema[datasetKey][dataKey];
        Object.keys(schemaData).forEach((key: string) => {
          if (schemaData[key].is_column && !columnKeys.includes(key)) {
            const column = JSON.parse(JSON.stringify({}));
            column.field = key;
            column.type = schemaData[key].is_array ? 'textArray' : schemaData[key].type;
            column.label = schemaData[key].display_name;
            column.width = 11.575;
            tableColumn.push(column);
            columnKeys.push(key);
          }
        });
      });
    });
  }
  return tableColumn;
};
